import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../ui/Logo';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

const navItems = [
  { to: '/catalog',   label: 'Marketplace' },
  { to: '/inventory', label: 'Mi Álbum' },
  { to: '/trades',    label: 'Intercambios' },
  { to: '/search',    label: 'Buscar' },
];

// ── SVG icons for bottom nav ──────────────────────────────────────────────────
const icons: Record<string, React.ReactNode> = {
  '/catalog': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  '/inventory': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  '/trades': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M7 16V4m0 0L3 8m4-4 4 4" />
      <path d="M17 8v12m0 0 4-4m-4 4-4-4" />
    </svg>
  ),
  '/search': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  '/profile': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

// ── Trades badge ──────────────────────────────────────────────────────────────
function TradesBadge({ userId, small = false }: { userId: string; small?: boolean }) {
  const { data } = useQuery({
    queryKey: ['trades-badge', userId],
    queryFn: async () => {
      const { data } = await api.get('/trades');
      const trades = data.data?.trades ?? [];
      return trades.filter(
        (t: { status: string; targetId: string }) =>
          t.status === 'PENDING' && t.targetId === userId
      ).length as number;
    },
    staleTime: 30_000,
    retry: false,
  });

  if (!data || data === 0) return null;

  return (
    <span className={[
      'absolute bg-red-500 text-white font-bold rounded-full flex items-center justify-center',
      small
        ? '-top-1 -right-1 w-3.5 h-3.5 text-[8px]'
        : '-top-2 -right-3 w-4 h-4 text-[10px]',
    ].join(' ')}>
      {data}
    </span>
  );
}

// ── NavBar ────────────────────────────────────────────────────────────────────
export function NavBar() {
  const { user, logout } = useAuth();

  return (
    <>
      {/* ── Top bar (all sizes) ── */}
      <nav className="bg-night shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-4">
          {/* Logo */}
          <NavLink to="/catalog" className="flex-shrink-0">
            <Logo size="sm" />
          </NavLink>

          {/* Divider — tablet+ only */}
          <div className="w-px h-6 bg-white/20 hidden sm:block" />

          {/* Nav links — tablet+ only */}
          <div className="hidden sm:flex items-center gap-1 flex-1">
            {navItems.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `nav-link${isActive ? ' nav-link-active' : ''}`
                }
              >
                <span className="relative">
                  {label}
                  {to === '/trades' && user?.userId && (
                    <TradesBadge userId={user.userId} />
                  )}
                </span>
              </NavLink>
            ))}
          </div>

          {/* Right: user + logout */}
          <div className="ml-auto flex items-center gap-3">
            <NavLink
              to="/profile"
              className="text-sm text-white/70 hover:text-gold-400 transition-colors font-semibold truncate max-w-[120px]"
            >
              {user?.username ?? 'Perfil'}
            </NavLink>
            <button
              onClick={logout}
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/10 text-white/80
                         hover:bg-white/20 hover:text-white transition-all"
            >
              Salir
            </button>
          </div>
        </div>
      </nav>

      {/* ── Bottom nav — mobile only ── */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-night border-t border-white/10 safe-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {navItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  'flex flex-col items-center justify-center gap-0.5 flex-1 h-full py-2 transition-colors',
                  isActive ? 'text-gold-400' : 'text-white/50 hover:text-white/80',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span className={['relative transition-transform', isActive ? 'scale-110' : ''].join(' ')}>
                    {icons[to]}
                    {to === '/trades' && user?.userId && (
                      <TradesBadge userId={user.userId} small />
                    )}
                  </span>
                  <span className="text-[10px] font-bold leading-none">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          {/* Profile link in bottom nav */}
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              [
                'flex flex-col items-center justify-center gap-0.5 flex-1 h-full py-2 transition-colors',
                isActive ? 'text-gold-400' : 'text-white/50 hover:text-white/80',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <span className={['transition-transform', isActive ? 'scale-110' : ''].join(' ')}>
                  {icons['/profile']}
                </span>
                <span className="text-[10px] font-bold leading-none">Perfil</span>
              </>
            )}
          </NavLink>
        </div>
      </nav>
    </>
  );
}
